import { router } from '@inertiajs/react'
import { useState, useEffect, useCallback, type ReactNode } from 'react'
import AppLayout from '@/layouts/AppLayout'
import AdminLayout from '@/layouts/AdminLayout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'

// --- Types ---

interface Synonym {
  id: string
  term: string
  level: string
  match_type: string
  confidence: number | null
}

interface Entry {
  id: string
  code: string
  grouping: string
  classification: string
  specialization: string | null
  display_name: string | null
  definition: string | null
  origin: string
  synonym_count: number
  provider_count: number
  synonyms: Synonym[]
}

interface SynonymRow {
  id: string
  term: string
  level: string
  match_type: string
  confidence: number | null
  entry: {
    code: string
    classification: string
    specialization: string | null
    grouping: string
  }
}

interface Pagination {
  page: number
  total_pages: number
  total: number
}

interface Stats {
  total_entries: number
  total_synonyms: number
  synonyms_by_match_type: Record<string, number>
  total_provider_tags: number
  groupings: string[]
  match_types: string[]
}

interface Filters {
  search?: string | null
  grouping?: string | null
  curated_only?: string | null
  match_type?: string | null
  level?: string | null
}

interface Props {
  tab: string
  stats: Stats
  entries?: Entry[]
  synonyms?: SynonymRow[]
  pagination: Pagination
  filters: Filters
}

// --- Match type badge colors ---

const matchTypeColors: Record<string, string> = {
  nucc_name: 'bg-gray-100 text-gray-800',
  abbreviation: 'bg-blue-100 text-blue-800',
  alternate_form: 'bg-indigo-100 text-indigo-800',
  curated_job_title: 'bg-amber-100 text-amber-800',
  trgm_derived: 'bg-cyan-100 text-cyan-800',
  llm_derived: 'bg-green-100 text-green-800',
  manual: 'bg-purple-100 text-purple-800',
}

function MatchTypeBadge({ type }: { type: string }) {
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded text-xs font-medium', matchTypeColors[type] || 'bg-gray-100 text-gray-800')}>
      {type.replace(/_/g, ' ')}
    </span>
  )
}

function LevelBadge({ level }: { level: string }) {
  const colors: Record<string, string> = {
    grouping: 'bg-orange-100 text-orange-800',
    classification: 'bg-sky-100 text-sky-800',
    specialization: 'bg-violet-100 text-violet-800',
  }
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded text-xs font-medium', colors[level] || 'bg-gray-100 text-gray-800')}>
      {level}
    </span>
  )
}

// --- Debounced search hook ---

function useDebouncedSearch(initialValue: string, delay: number, onSearch: (value: string) => void) {
  const [value, setValue] = useState(initialValue)

  useEffect(() => {
    const timer = setTimeout(() => {
      onSearch(value)
    }, delay)
    return () => clearTimeout(timer)
  }, [value, delay]) // eslint-disable-line react-hooks/exhaustive-deps

  return [value, setValue] as const
}

// --- Main Component ---

export default function TaxonomyBrowserIndex({ tab, stats, entries, synonyms, pagination, filters }: Props) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())

  const navigate = useCallback((params: Record<string, string | undefined>) => {
    const cleaned: Record<string, string> = {}
    for (const [k, v] of Object.entries(params)) {
      if (v != null && v !== '' && v !== 'false') cleaned[k] = v
    }
    router.get('/admin/taxonomy_browser', cleaned, { preserveState: true })
  }, [])

  // Build current params for navigation
  const currentParams = useCallback((): Record<string, string | undefined> => {
    if (tab === 'synonyms') {
      return {
        tab: 'synonyms',
        search: filters.search || undefined,
        match_type: filters.match_type || undefined,
        level: filters.level || undefined,
      }
    }
    return {
      tab: tab === 'entries' ? undefined : tab,
      search: filters.search || undefined,
      grouping: filters.grouping || undefined,
      curated_only: filters.curated_only || undefined,
    }
  }, [tab, filters])

  const [searchValue, setSearchValue] = useDebouncedSearch(
    filters.search || '',
    300,
    (val) => {
      const params = currentParams()
      params.search = val || undefined
      params.page = undefined
      navigate(params)
    }
  )

  function switchTab(newTab: string) {
    navigate(newTab === 'entries' ? {} : { tab: newTab })
  }

  function goToPage(page: number) {
    const params = currentParams()
    params.page = page > 1 ? String(page) : undefined
    navigate(params)
  }

  function toggleRow(id: string) {
    setExpandedRows(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function onFilterChange(key: string, value: string) {
    const params = currentParams()
    params[key] = value || undefined
    params.page = undefined
    navigate(params)
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-6">Taxonomy Browser</h1>

      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <StatCard label="Entries" value={stats.total_entries} />
        <StatCard label="Synonyms" value={stats.total_synonyms} />
        <StatCard label="Providers Tagged" value={stats.total_provider_tags} />
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 mb-6 border-b border-border">
        {(['entries', 'synonyms'] as const).map((t) => (
          <button
            key={t}
            onClick={() => switchTab(t)}
            className={cn(
              'flex items-center gap-2 px-3 py-2 text-sm font-medium transition-colors -mb-px capitalize',
              tab === t
                ? 'border-b-2 border-foreground text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'entries' ? (
        <EntriesTab
          entries={entries || []}
          pagination={pagination}
          filters={filters}
          groupings={stats.groupings}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          expandedRows={expandedRows}
          toggleRow={toggleRow}
          onFilterChange={onFilterChange}
          goToPage={goToPage}
        />
      ) : (
        <SynonymsTab
          synonyms={synonyms || []}
          pagination={pagination}
          filters={filters}
          matchTypes={stats.match_types}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          onFilterChange={onFilterChange}
          goToPage={goToPage}
        />
      )}
    </div>
  )
}

// --- Stat Card ---

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border p-4">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value.toLocaleString()}</div>
    </div>
  )
}

// --- Pagination ---

function PaginationControls({ pagination, goToPage }: { pagination: Pagination; goToPage: (p: number) => void }) {
  return (
    <div className="flex items-center justify-between mt-4 text-sm">
      <span className="text-muted-foreground">
        {pagination.total.toLocaleString()} total
      </span>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={pagination.page <= 1}
          onClick={() => goToPage(pagination.page - 1)}
        >
          Prev
        </Button>
        <span className="text-muted-foreground">
          Page {pagination.page} of {pagination.total_pages}
        </span>
        <Button
          size="sm"
          variant="outline"
          disabled={pagination.page >= pagination.total_pages}
          onClick={() => goToPage(pagination.page + 1)}
        >
          Next
        </Button>
      </div>
    </div>
  )
}

// --- Entries Tab ---

interface EntriesTabProps {
  entries: Entry[]
  pagination: Pagination
  filters: Filters
  groupings: string[]
  searchValue: string
  setSearchValue: (v: string) => void
  expandedRows: Set<string>
  toggleRow: (id: string) => void
  onFilterChange: (key: string, value: string) => void
  goToPage: (p: number) => void
}

function EntriesTab({ entries, pagination, filters, groupings, searchValue, setSearchValue, expandedRows, toggleRow, onFilterChange, goToPage }: EntriesTabProps) {
  return (
    <>
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          value={filters.grouping || ''}
          onChange={(e) => onFilterChange('grouping', e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">All groupings</option>
          {groupings.map((g) => (
            <option key={g} value={g}>{g}</option>
          ))}
        </select>

        <Input
          placeholder="Search classification, specialization, code..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          className="w-72"
        />

        <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={filters.curated_only === 'true'}
            onChange={(e) => onFilterChange('curated_only', e.target.checked ? 'true' : '')}
            className="rounded border-input"
          />
          No curated synonyms
        </label>
      </div>

      {entries.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">No entries match your filters.</p>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Classification</TableHead>
                <TableHead>Specialization</TableHead>
                <TableHead>Display Name</TableHead>
                <TableHead className="text-right">Synonyms</TableHead>
                <TableHead className="text-right">Providers</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) => (
                <EntryRow
                  key={entry.id}
                  entry={entry}
                  expanded={expandedRows.has(entry.id)}
                  onToggle={() => toggleRow(entry.id)}
                />
              ))}
            </TableBody>
          </Table>
          <PaginationControls pagination={pagination} goToPage={goToPage} />
        </>
      )}
    </>
  )
}

// --- Entry Row with Expansion ---

function EntryRow({ entry, expanded, onToggle }: { entry: Entry; expanded: boolean; onToggle: () => void }) {
  return (
    <>
      <TableRow className="cursor-pointer hover:bg-muted/50" onClick={onToggle}>
        <TableCell className="font-mono text-sm">{entry.code}</TableCell>
        <TableCell className="font-medium">{entry.classification}</TableCell>
        <TableCell className="text-muted-foreground">{entry.specialization || '—'}</TableCell>
        <TableCell className="text-sm">{entry.display_name || '—'}</TableCell>
        <TableCell className="text-right">
          <Badge variant="secondary" className="text-xs px-1.5 py-0">
            {entry.synonym_count}
          </Badge>
        </TableCell>
        <TableCell className="text-right font-mono text-sm">{entry.provider_count}</TableCell>
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell colSpan={6} className="bg-muted/30 p-4">
            {entry.definition && (
              <p className="text-sm text-muted-foreground mb-3 italic">{entry.definition}</p>
            )}
            {entry.synonyms.length > 0 ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground">
                    <th className="pb-1 font-medium">Term</th>
                    <th className="pb-1 font-medium">Match Type</th>
                    <th className="pb-1 font-medium">Level</th>
                    <th className="pb-1 font-medium text-right">Confidence</th>
                  </tr>
                </thead>
                <tbody>
                  {entry.synonyms.map((s) => (
                    <tr key={s.id} className="border-t border-border/50">
                      <td className="py-1.5">{s.term}</td>
                      <td className="py-1.5"><MatchTypeBadge type={s.match_type} /></td>
                      <td className="py-1.5"><LevelBadge level={s.level} /></td>
                      <td className="py-1.5 text-right text-muted-foreground">
                        {s.confidence != null ? s.confidence.toFixed(2) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-muted-foreground">No synonyms for this entry.</p>
            )}
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

// --- Synonyms Tab ---

interface SynonymsTabProps {
  synonyms: SynonymRow[]
  pagination: Pagination
  filters: Filters
  matchTypes: string[]
  searchValue: string
  setSearchValue: (v: string) => void
  onFilterChange: (key: string, value: string) => void
  goToPage: (p: number) => void
}

function SynonymsTab({ synonyms, pagination, filters, matchTypes, searchValue, setSearchValue, onFilterChange, goToPage }: SynonymsTabProps) {
  return (
    <>
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select
          value={filters.match_type || ''}
          onChange={(e) => onFilterChange('match_type', e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">All match types</option>
          {matchTypes.map((mt) => (
            <option key={mt} value={mt}>{mt.replace(/_/g, ' ')}</option>
          ))}
        </select>

        <select
          value={filters.level || ''}
          onChange={(e) => onFilterChange('level', e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">All levels</option>
          <option value="grouping">grouping</option>
          <option value="classification">classification</option>
          <option value="specialization">specialization</option>
        </select>

        <Input
          placeholder="Search synonyms..."
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          className="w-72"
        />
      </div>

      {synonyms.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">No synonyms match your filters.</p>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Term</TableHead>
                <TableHead>Maps To</TableHead>
                <TableHead>Grouping</TableHead>
                <TableHead>Level</TableHead>
                <TableHead>Match Type</TableHead>
                <TableHead className="text-right">Confidence</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {synonyms.map((s) => (
                <TableRow key={s.id}>
                  <TableCell className="font-medium">{s.term}</TableCell>
                  <TableCell className="text-sm">
                    {s.entry.classification}
                    {s.entry.specialization && (
                      <span className="text-muted-foreground"> / {s.entry.specialization}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{s.entry.grouping}</TableCell>
                  <TableCell><LevelBadge level={s.level} /></TableCell>
                  <TableCell><MatchTypeBadge type={s.match_type} /></TableCell>
                  <TableCell className="text-right text-muted-foreground font-mono text-sm">
                    {s.confidence != null ? s.confidence.toFixed(2) : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <PaginationControls pagination={pagination} goToPage={goToPage} />
        </>
      )}
    </>
  )
}

TaxonomyBrowserIndex.layout = (page: ReactNode) => (
  <AppLayout>
    <AdminLayout>{page}</AdminLayout>
  </AppLayout>
)
